import { Activity } from '@cube3/common/model/schema/resources/activities';
import {
  ASSET_ACTIONS,
  FOLDER_ACTIONS,
  PROJECT_ACTIONS,
  SHARE_ACTIONS
} from '@cube3/common/model/types/activity-types';
import { ProjectStatuses } from '@cube3/state/types';

enum Verbs {
  /// exceptions
  ACCOUNTCREATED = 'JOINED',
  ACCOUNTDELETED = 'REMOVED',
  ACCOUNTUPDATED = 'ACCOUNTUPDATED',
  //
  PROJECTCREATED = 'ADDED',
  PROJECTUPDATED = 'PROJECTUPDATED',
  //
  SHARECREATED = 'SHARECREATED',
  SHAREDELETED = 'SHAREDELETED',
  COMMENTCREATED = 'COMMENTCREATED',
  COMMENTUPDATED = 'COMMENTUPDATED',
  COMMENTDELETED = 'COMMENTDELETED',
  COMMENTRESOLVED = 'COMMENTRESOLVED',
  APPROVALUPDATED = 'APPROVALUPDATED',
  APPROVALCREATED = 'APPROVALCREATED',
  //
  EXPORTSUCCESS = 'EXPORTED',
  EXPORTFAILED = 'EXPORTFAILED',
  //
  CREATED = 'ADDED',
  DELETED = 'DELETED',
  UPDATED = 'UPDATED',
  DOWNLOADED = 'DOWNLOADED',
  PREVIEWED = 'VIEWED',
  TRASHED = 'TRASHED',
  RESTORED = 'RESTORED',
  LINKED = 'LINKED',
  UNLINKED = 'UNLINKED'
}

const changedAttribute = {
  message: 'comment',
  display_name: 'name',
  is_public: 'is_public',
  project_status: 'project_status'
};

// special:
// ApprovalDeleted: {Actor} cleared {Asset}'s approval state in {Folder} at {Time}
/** @action: ActivityAction
 * @intent: `share` intent || `approval` status || comment resolution
 */
const getShareVerb = (activity: Activity, intent) => {
  const action = activity?.action;
  const isCreated = action === SHARE_ACTIONS.ShareCreated;
  const isDeleted = action === SHARE_ACTIONS.ShareDeleted;
  switch (action) {
    case 'ApprovalUpdated':
    case 'ApprovalCreated':
      return activity['status'] || intent?.status;
    case 'CommentDeleted':
      return 'removed comment';
    case 'CommentCreated':
      return intent?.resolution ? intent.resolution + 'd comment' : 'commented';
    case 'CommentResolved': // NOTE: not implemented, use `CommentCreated` + resolution instead
      return 'resolved comment';
    case 'CommentUpdated':
      return 'changed comment';
    default:
      break;
  }
  if (isCreated || isDeleted) {
    switch (intent) {
      case 'review-legacy':
      case 'review':
        return isCreated ? 'requested review for' : 'closed Review of';
      case 'request':
        return isCreated ? 'requested files for' : 'closed File Request for';
      default:
        return isCreated ? 'shared' : 'closed Share Link for';
    }
  }

  return '';
};

const toCrud = (action) => {
  for (const verb in Verbs) {
    if (action.toLowerCase().includes(verb.toLowerCase())) {
      return Verbs[verb];
    }
  }
};

export const getChangedValue = (activity: Activity) => {
  const changes = activity?.changes;
  if (!changes) return null;
  const changedAttri = changes[0].origin;
  const newVal = changes[0].values.new?.[0];
  const oldVal = changes[0].values.old?.[0];

  return { newVal, oldVal, name: changedAttri };
};

export const getVerb = (activity: Activity, intent?) => {
  const { action } = activity;
  if (!action) return '';
  switch (action) {
    // handle project changes
    case PROJECT_ACTIONS.ProjectUpdated: {
      const { name, newVal } = getChangedValue(activity);
      if (name === 'is_public') {
        return newVal ? 'made public' : 'made private';
      }
      if (name === 'project_status') {
        return newVal === ProjectStatuses.ProjectStatusArchived
          ? 'archived'
          : 'unarchived';
      }
      break;
    }
    default:
      break;
  }

  return getShareVerb(activity, intent) || toCrud(action).toLowerCase();
};

export const getPrep = (action: string) => {
  if (!action) return '';
  switch (toCrud(action).toLowerCase()) {
    case 'exported':
    case 'restored':
    case 'added':
    case 'created':
      return 'to';
    case 'deleted':
    case 'trashed':
    case 'downloaded':
    case 'sharecreated':
    case 'shared':
    case 'commentdeleted':
      return 'from';
    case 'commentupdated':
    case 'commentresolved':
    case 'commentcreated':
      return 'on';
    case 'approvalupdated':
    case 'approvalcreated':
      return '';
    default:
      return 'in';
  }
};

/**
 * - active pattern: `{Actor} shared {Share} from {Project} at {Time}`,
 * - passive pattern: `{Asset} was added to {Project} by {Actor} at {Time}`
 */
export const getPattern = (action, context) => {
  switch (context) {
    case 'workspace':
    case 'project':
      return Object.hasOwn(
        { ...ASSET_ACTIONS, ...FOLDER_ACTIONS, ...PROJECT_ACTIONS },
        action
      )
        ? 'passive'
        : 'active';

    default:
      return 'active';
  }
};
